import { useCallback, useEffect, useRef, useState } from "react";

interface Envelope<T> {
  v: number;
  data: T;
}

export interface PersistOptions<T> {
  /** Bump when the shape changes; mismatched versions fall back to initial. */
  version?: number;
  /** Optional migration from an older envelope to the current shape. */
  migrate?: (data: unknown, fromVersion: number) => T | null;
}

function read<T>(key: string, version: number, migrate?: PersistOptions<T>["migrate"]): T | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    // Private-mode Safari and "block site data" both throw on access.
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Envelope<T> | T;
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "v" in parsed &&
      "data" in parsed
    ) {
      const env = parsed as Envelope<T>;
      if (env.v === version) return env.data;
      return migrate ? migrate(env.data, env.v) : null;
    }
    // Pre-envelope value written by an older build.
    return migrate ? migrate(parsed, 0) : null;
  } catch {
    return null;
  }
}

/**
 * Typed, versioned localStorage state.
 *
 * Corrupt or outdated data resolves to `initial` instead of throwing — a stale
 * key must never white-screen an app the user has already put work into.
 */
export function useLocalStorage<T>(
  key: string,
  initial: T,
  options: PersistOptions<T> = {},
): [T, (value: T | ((prev: T) => T)) => void, { clear: () => void }] {
  const { version = 1, migrate } = options;
  const migrateRef = useRef(migrate);
  migrateRef.current = migrate;

  const [value, setValue] = useState<T>(() => {
    const stored = read<T>(key, version, migrate);
    return stored === null ? initial : stored;
  });

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    try {
      const envelope: Envelope<T> = { v: version, data: value };
      window.localStorage.setItem(key, JSON.stringify(envelope));
    } catch {
      // Quota exceeded or storage blocked. State stays in memory; the app works,
      // it just won't survive a reload. Silently degrading beats crashing.
    }
  }, [key, value, version]);

  // Keep duplicate tabs of the same app in sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) return;
      const next = read<T>(key, version, migrateRef.current);
      if (next !== null) setValue(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, version]);

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    setValue(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) =>
      typeof next === "function" ? (next as (p: T) => T)(prev) : next,
    );
  }, []);

  return [value, update, { clear }];
}
